import { Injectable, Inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { SURVEY_API_BASE_URL } from './survey-api-base-url.token';
import { LanguageService } from '../services/language/language.service';

export interface SurveyOption {
  id: number;
  optionText: string;
  optionOrder: number;
  createdAt: string;
  updatedAt: string;
}

export interface SurveySlider {
  id: number;
  minValue: number;
  maxValue: number;
  minLabel: string;
  maxLabel: string;
  createdAt: string;
  updatedAt: string;
}

export interface SurveyQuestion {
  id: number;
  type: 'Open' | 'SingleChoice' | 'MultipleChoice' | 'Slider';
  isRequired: boolean;
  questionText: string;
  questionOrder: number;
  isBold: boolean;
  isItalic: boolean;
  isUnderline: boolean;
  options: SurveyOption[];
  slider: SurveySlider | null;
  createdAt: string;
  updatedAt: string;
}

export interface SurveyData {
  id: number;
  surveyGroupId?: number;
  name: string;
  description: string;
  userMessage: string;
  publishDate: string;
  endDate: string;
  backgroundImageUrl: string | null;
  createdAt: string;
  updatedAt: string;
  numberOfRespondents: number;
  responseRate: number;
  language: {
    id: number;
    name: string;
    symbol: string;
    createdAt: string;
    updatedAt: string;
  };
  status: {
    id: number;
    name: string;
    createdAt: string;
    updatedAt: string;
  };
  questions: SurveyQuestion[];
  surveyTriggerPages: unknown[];
  surveyTriggerPlannerDestinations: unknown[];
  surveyTriggerPlannerLines: unknown[];
  surveyTriggerCities: unknown[];
  surveyTriggerPlannerStations: unknown[];
  surveyTriggerTaxis: unknown[];
}

export interface SurveyOpenQuestionAnswer {
  openText: string;
}

export interface SurveyOptionQuestionAnswer {
  questionOptionId: number;
}

export interface SurveySliderQuestionAnswer {
  value: number;
}

export interface SurveyAnswerItem {
  surveyQuestionId: number;
  surveyOpenQuestionAnswer?: SurveyOpenQuestionAnswer;
  surveyOptionQuestionAnswer?: SurveyOptionQuestionAnswer[];
  surveySliderQuestionAnswer?: SurveySliderQuestionAnswer;
}

export interface SurveyResponse {
  deviceUuid: string;
  deviceType: 'Mobile' | 'Desktop';
  userLocation?: string;
  answers: SurveyAnswerItem[];
}

export interface SurveyGroupItem {
  surveys: SurveyData[];
  surveyTriggerPages: unknown[];
  surveyTriggerPlannerDestinations: unknown[];
  surveyTriggerPlannerLines: unknown[];
  surveyTriggerCities: unknown[];
  surveyTriggerPlannerStations: unknown[];
  surveyTriggerTaxis: unknown[];
  id: number;
  name: string;
  uploadedFileReferenceId: string | null;
  publishDate: string;
  endDate: string;
  backgroundImageUrl: string | null;
  createdAt: string;
  updatedAt: string;
  numberOfRespondents: number;
  responseRate: number;
  status: {
    id: number;
    name: string;
    createdAt: string;
    updatedAt: string;
  };
}

export interface PollsApiResponse {
  items: SurveyGroupItem[];
  totalItems: number;
  page: number;
  pageSize: number;
  totalPages: number;
}

@Injectable({
  providedIn: 'root',
})
export class SurveyService {
  constructor(
    private readonly http: HttpClient,
    private readonly languageService: LanguageService,
    @Inject(SURVEY_API_BASE_URL) private readonly baseUrl: string
  ) {}

  private getCurrentLocaleSymbol(): string {
    return this.languageService.getSelectedLanguage()?.symbol ?? 'he';
  }

  private getTranslationId(): number {
    const sym = this.getCurrentLocaleSymbol();
    switch (sym) {
      case 'he':
        return 1;
      case 'en':
        return 2;
      case 'ar':
        return 3;
      default:
        return 1;
    }
  }

  private mergeGroupSurvey(group: SurveyGroupItem, survey: SurveyData): SurveyData {
    const mergedSurvey: SurveyData = {
      ...survey,
      id: group.id,
      surveyGroupId: survey.surveyGroupId || group.id,
      name: group.name,
      publishDate: group.publishDate,
      endDate: group.endDate,
      backgroundImageUrl: group.backgroundImageUrl,
      createdAt: group.createdAt,
      updatedAt: group.updatedAt,
      numberOfRespondents: group.numberOfRespondents,
      responseRate: group.responseRate,
      status: group.status,
      surveyTriggerPages: group.surveyTriggerPages,
      surveyTriggerPlannerDestinations: group.surveyTriggerPlannerDestinations,
      surveyTriggerPlannerLines: group.surveyTriggerPlannerLines,
      surveyTriggerCities: group.surveyTriggerCities,
      surveyTriggerPlannerStations: group.surveyTriggerPlannerStations,
      surveyTriggerTaxis: group.surveyTriggerTaxis,
    };
    if (mergedSurvey.questions) {
      mergedSurvey.questions.sort((a, b) => a.questionOrder - b.questionOrder);
    }
    return mergedSurvey;
  }

  getAllSurveys(): Observable<SurveyData[]> {
    const url = `${this.baseUrl}/polls`;
    const currentLocale = this.getCurrentLocaleSymbol();

    return this.http.get<PollsApiResponse>(url).pipe(
      map((response) => {
        const surveys: SurveyData[] = [];
        response.items.forEach((group) => {
          const surveyForLocale = group.surveys.find(
            (s) => s.language.symbol === currentLocale
          );
          const survey = surveyForLocale || group.surveys[0];
          if (survey) {
            surveys.push(this.mergeGroupSurvey(group, survey));
          }
        });
        return surveys;
      }),
      catchError((error) => {
        console.error('Error fetching all surveys:', error);
        return of([]);
      })
    );
  }

  getSurvey(surveyId: number): Observable<SurveyData | null> {
    const url = `${this.baseUrl}/polls/${surveyId}`;
    const currentLocale = this.getCurrentLocaleSymbol();

    return this.http.get<SurveyGroupItem>(url).pipe(
      map((group) => {
        if (!group) {
          return null;
        }
        const surveyForLocale = group.surveys?.find(
          (s) => s.language.symbol === currentLocale
        );
        const survey = surveyForLocale || group.surveys?.[0];
        if (!survey) {
          return null;
        }
        return this.mergeGroupSurvey(group, survey);
      }),
      catchError((error) => {
        console.error('Error fetching survey:', { surveyId, error });
        return of(null);
      })
    );
  }

  recordSurveyView(groupId: number): Observable<unknown> {
    const translationId = this.getTranslationId();
    const url = `${this.baseUrl}/polls/${groupId}/${translationId}/views`;
    return this.http.post(url, {}).pipe(
      catchError((error) => {
        console.error('Error recording survey view:', error);
        return of(null);
      })
    );
  }

  submitSurveyResponse(pollId: number, response: SurveyResponse): Observable<unknown> {
    const translationId = this.getTranslationId();
    const url = `${this.baseUrl}/polls/${pollId}/${translationId}/response`;

    return this.http.post(url, response).pipe(
      catchError((error) => {
        console.error('Error submitting survey response:', error);
        throw error;
      })
    );
  }

  detectDeviceType(): 'Mobile' | 'Desktop' {
    if (typeof window === 'undefined' || !window.navigator) {
      return 'Desktop';
    }
    try {
      const isMobile = window.innerWidth < 768;
      const userAgent =
        navigator.userAgent || navigator.vendor || (window as unknown as { opera?: string }).opera || '';
      const isMobileUserAgent =
        /android|webos|iphone|ipad|ipod|blackberry|iemobile|opera mini/i.test(
          userAgent.toLowerCase()
        );
      return isMobile || isMobileUserAgent ? 'Mobile' : 'Desktop';
    } catch {
      return 'Desktop';
    }
  }

  isSurveyActive(survey: SurveyData): boolean {
    if (!survey) return false;
    const now = new Date();
    const publishDate = new Date(survey.publishDate);
    const endDate = new Date(survey.endDate);
    return now >= publishDate && now <= endDate;
  }
}
